// DEBT-VITA™ — Page hôte du module Dette & Vies
// Onglets : Paramètres (formulaire) | Dashboard (8 visualisations)
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Coins, Settings as SettingsIcon, BarChart3, Scale } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import DebtParametersForm from "@/components/debt/DebtParametersForm";
import DebtVitaDashboard from "@/components/debt/DebtVitaDashboard";
import DebtComparator from "@/components/debt/DebtComparator";
import { getProject } from "@/lib/profiles";
import { pingFeature } from "@/lib/admin-api";

export default function DebtVita() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState("dashboard");

  useEffect(() => {
    let cancelled = false;
    getProject(projectId)
      .then((p) => {
        if (!cancelled) setProject(p);
      })
      .catch(() => {});
    pingFeature("debt_vita.opened", { project_id: projectId });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return (
    <div className="container mx-auto max-w-6xl px-4 py-6 lg:py-10" data-testid="debt-vita-page">
      <Link
        to={`/app/projects/${projectId}/wizard/results`}
        className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground"
        data-testid="debt-back-to-results"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Retour aux résultats
      </Link>

      <header className="mt-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-red-100 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-red-800 dark:bg-red-950/50 dark:text-red-300">
            <Coins className="h-3.5 w-3.5" />
            DEBT-VITA™
          </div>
          <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl">
            Dette & Vies
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Mesure du poids de l'endettement public en{" "}
            <em>vies humaines hypothéquées</em>.
            {project?.name ? (
              <>
                {" "}
                Projet :{" "}
                <strong className="text-foreground">{project.name}</strong>
              </>
            ) : null}
          </p>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="grid w-full max-w-2xl grid-cols-3" data-testid="debt-tabs">
          <TabsTrigger value="dashboard" data-testid="debt-tab-dashboard">
            <BarChart3 className="mr-2 h-4 w-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="compare" data-testid="debt-tab-compare">
            <Scale className="mr-2 h-4 w-4" />
            Comparer
          </TabsTrigger>
          <TabsTrigger value="parameters" data-testid="debt-tab-parameters">
            <SettingsIcon className="mr-2 h-4 w-4" />
            Paramètres
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-5">
          <DebtVitaDashboard projectId={projectId} />
        </TabsContent>

        <TabsContent value="compare" className="mt-5">
          <DebtComparator />
        </TabsContent>

        <TabsContent value="parameters" className="mt-5">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Configuration des paramètres de la dette
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DebtParametersForm
                projectId={projectId}
                onCalculateClicked={() => setTab("dashboard")}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
